import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, UserCog, ShieldCheck, MonitorSmartphone, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ROLES = [
  "admin",
  "sales",
  "warehouse",
  "it_support",
  "finance",
  "office_support",
];

// Friendly labels for role select/pill.
const ROLE_LABELS = {
  admin: "Admin",
  sales: "Sales",
  warehouse: "Warehouse",
  it_support: "IT Support Engineering",
  finance: "Finance",
  office_support: "Office Support",
};

// Friendly labels for each module key returned by /auth/modules-catalog.
const MODULE_LABELS = {
  dashboard: "Dashboard",
  clients: "Clients",
  attendance: "Client Attendance",
  inventory: "General Inventory",
  quotations: "Quotations",
  orders: "Client Orders",
  delivery_orders: "Delivery Orders",
  invoices: "Invoices",
  suppliers: "Suppliers",
  rfqs: "Requests for Quotation",
  purchase_orders: "Purchase Orders",
  expenses: "Expenses",
  loans: "Financing",
  bills: "Rent & Bills",
  software_licences: "Software Licences",
  company_assets: "Company Assets",
  reports: "Reports",
  payment_claims: "Payment Claims",
  project_funds: "Project Funds",
  payroll: "Payroll & Payslips",
  leave: "Leave Requests",
  users: "Users & Roles",
  settings: "System Settings",
};

const emptyForm = {
  email: "",
  first_name: "",
  last_name: "",
  role: "warehouse",
  password: "",
  active: true,
  permissions: [],
};

export default function Users() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: api.listUsers });
  const { data: catalog } = useQuery({
    queryKey: ["modules-catalog"],
    queryFn: () => api.modulesCatalog(),
  });
  const modules = catalog?.modules || [];
  const roleDefaults = catalog?.role_defaults || {};

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Auto-fill permissions with role defaults whenever the role changes AND the
  // user hasn't already customised the list (i.e. list is empty or exactly
  // matches an old default).
  const [permsTouched, setPermsTouched] = useState(false);
  useEffect(() => {
    if (permsTouched) return;
    const defaults = roleDefaults[form.role] || [];
    setForm((f) => ({ ...f, permissions: defaults }));
  }, [form.role, roleDefaults, permsTouched]);

  const createMut = useMutation({
    mutationFn: (body) => api.createUser(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setOpen(false);
      toast.success("User created");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.updateUser(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      setOpen(false);
      setEditing(null);
      toast.success("Updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  function openCreate() {
    setEditing(null);
    setPermsTouched(false);
    setForm({ ...emptyForm, permissions: roleDefaults.warehouse || [] });
    setOpen(true);
  }
  function openEdit(u) {
    setEditing(u);
    setPermsTouched(true); // don't override on edit
    setForm({
      email: u.email,
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      role: u.role,
      password: "",
      active: u.active !== false,
      permissions: u.permissions || [],
    });
    setOpen(true);
  }

  function togglePerm(mod) {
    setPermsTouched(true);
    setForm((f) => {
      const set = new Set(f.permissions);
      if (set.has(mod)) set.delete(mod);
      else set.add(mod);
      // preserve catalog order for stable UI
      return { ...f, permissions: modules.filter((m) => set.has(m)) };
    });
  }
  function selectAllPerms() {
    setPermsTouched(true);
    setForm((f) => ({ ...f, permissions: [...modules] }));
  }
  function clearAllPerms() {
    setPermsTouched(true);
    setForm((f) => ({ ...f, permissions: [] }));
  }
  function applyRoleDefaults() {
    setPermsTouched(false); // let the effect re-apply
    setForm((f) => ({ ...f, permissions: roleDefaults[f.role] || [] }));
  }

  function submit() {
    if (!form.first_name.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!editing && !form.password) {
      toast.error("Password is required for new users");
      return;
    }
    const body = { ...form };
    if (editing && !body.password) delete body.password;
    if (editing) updateMut.mutate({ id: editing.id, body });
    else createMut.mutate(body);
  }

  const isAdminRow = form.role === "admin";

  return (
    <div data-testid="users-page">
      <PageHeader
        overline="admin"
        title="Users & Roles"
        subtitle="Create staff accounts, assign a base role, and manually tick the modules each user can access."
        actions={
          <Button
            data-testid="new-user-btn"
            onClick={openCreate}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="size-4 mr-1.5" /> New user
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : data.length === 0 ? (
        <EmptyState title="No users yet" />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="users-table-wrap">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Name</TableHead>
                <TableHead className="overline">Email</TableHead>
                <TableHead className="overline">Role</TableHead>
                <TableHead className="overline">Module access</TableHead>
                <TableHead className="overline">Active</TableHead>
                <TableHead className="overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((u) => {
                const fullName =
                  [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "—";
                const perms = u.permissions || [];
                const totalMods = modules.length || 14;
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-2">
                        <span className="size-7 grid place-items-center rounded-md bg-primary/10 text-primary">
                          <UserCog className="size-3.5" />
                        </span>
                        {fullName}
                      </span>
                    </TableCell>
                    <TableCell className="mono text-xs">{u.email}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 ring-inset ${
                          u.role === "admin"
                            ? "bg-red-50 text-red-700 ring-red-200"
                            : u.role === "sales"
                            ? "bg-amber-50 text-amber-800 ring-amber-200"
                            : u.role === "warehouse"
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                            : u.role === "it_support"
                            ? "bg-sky-50 text-sky-800 ring-sky-200"
                            : u.role === "finance"
                            ? "bg-indigo-50 text-indigo-800 ring-indigo-200"
                            : u.role === "office_support"
                            ? "bg-purple-50 text-purple-800 ring-purple-200"
                            : "bg-slate-100 text-slate-700 ring-slate-200"
                        }`}
                      >
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </TableCell>
                    <TableCell>
                      {u.role === "admin" ? (
                        <span className="inline-flex items-center gap-1 text-xs text-red-700">
                          <ShieldCheck className="size-3" /> Full access
                        </span>
                      ) : perms.length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">
                          No modules
                        </span>
                      ) : (
                        <span
                          data-testid={`user-perms-summary-${u.id}`}
                          className="inline-block px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200"
                          title={perms.map((p) => MODULE_LABELS[p] || p).join(", ")}
                        >
                          {perms.length} / {totalMods}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.active === false ? (
                        <span className="text-xs text-red-600">No</span>
                      ) : (
                        <span className="text-xs text-emerald-700">Yes</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`edit-user-${u.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(u)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`delete-user-${u.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => {
                          if (window.confirm("Delete user?")) deleteMut.mutate(u.id);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <ActiveSessionsCard />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="user-form-dialog"
          className="max-w-2xl max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle>
            <DialogDescription className="text-xs">
              {editing
                ? "Leave password empty to keep the current one."
                : "Fill in the details and tick the modules the user should have access to."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">First name *</Label>
                <Input
                  data-testid="user-form-first-name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label className="overline">Last name</Label>
                <Input
                  data-testid="user-form-last-name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label className="overline">Email *</Label>
              <Input
                data-testid="user-form-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Role *</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => {
                    setForm({ ...form, role: v });
                  }}
                >
                  <SelectTrigger data-testid="user-form-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">
                  {editing ? "New password (optional)" : "Password *"}
                </Label>
                <Input
                  data-testid="user-form-password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                data-testid="user-form-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Active
            </label>

            {/* PERMISSION ACCESS BY (manual module assignment) */}
            <div
              data-testid="user-form-permissions"
              className="rounded-md border border-border bg-slate-50 p-4"
            >
              <div className="flex items-start justify-between mb-3 gap-3">
                <div>
                  <p className="overline text-primary">Permission access by module</p>
                  <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                    Tick each module this user is allowed to see. Admins always have
                    access to every module regardless of ticks.
                  </p>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button
                    data-testid="user-form-perms-defaults"
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] px-2"
                    onClick={applyRoleDefaults}
                    disabled={isAdminRow}
                    title="Reset to the default set for this role"
                  >
                    Role defaults
                  </Button>
                  <Button
                    data-testid="user-form-perms-all"
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] px-2"
                    onClick={selectAllPerms}
                    disabled={isAdminRow}
                  >
                    Select all
                  </Button>
                  <Button
                    data-testid="user-form-perms-clear"
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] px-2"
                    onClick={clearAllPerms}
                    disabled={isAdminRow}
                  >
                    Clear
                  </Button>
                </div>
              </div>
              {isAdminRow ? (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-800 inline-flex items-center gap-2">
                  <ShieldCheck className="size-3.5" />
                  <span>
                    Admin role grants unconditional access to every module.
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {modules.map((m) => {
                    const checked = form.permissions.includes(m);
                    return (
                      <label
                        key={m}
                        data-testid={`user-form-perm-${m}`}
                        className={`flex items-center gap-2 text-xs cursor-pointer px-2.5 py-1.5 rounded-md border transition ${
                          checked
                            ? "bg-primary/5 border-primary/40 text-foreground"
                            : "bg-white border-border text-slate-700 hover:border-primary/30"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePerm(m)}
                          className="accent-red-600"
                        />
                        <span className="truncate">{MODULE_LABELS[m] || m}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {!isAdminRow && (
                <p
                  data-testid="user-form-perms-count"
                  className="text-[10px] text-muted-foreground mt-2"
                >
                  {form.permissions.length} of {modules.length} modules selected
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="user-form-cancel"
            >
              Cancel
            </Button>
            <Button
              data-testid="user-form-save"
              className="bg-primary hover:bg-primary/90"
              onClick={submit}
              disabled={createMut.isPending || updateMut.isPending}
            >
              {editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ActiveSessionsCard() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data = [], isLoading } = useQuery({
    queryKey: ["active-sessions"],
    queryFn: api.activeSessions,
    refetchInterval: 60_000,
  });
  const revokeMut = useMutation({
    mutationFn: (userId) => api.revokeUserSessions(userId),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["active-sessions"] });
      toast.success(`All sessions revoked for ${r.user}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not revoke sessions"),
  });

  function revoke(s) {
    const self = s.user_id === me?.id;
    const msg = self
      ? "This is YOUR account — revoking will sign you out everywhere, including here. Continue?"
      : `Sign out ${s.email} on every device?`;
    if (window.confirm(msg)) revokeMut.mutate(s.user_id);
  }

  return (
    <div className="mt-8 border border-border rounded-md bg-white" data-testid="active-sessions-card">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <MonitorSmartphone className="size-4 text-primary" />
        <h3 className="font-display font-semibold">Active sessions</h3>
        <span className="text-[10px] mono px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
          {data.length}
        </span>
        <span className="text-xs text-muted-foreground ml-2">
          One row per signed-in user (last 7 days, valid tokens). Revoking signs that user out everywhere.
        </span>
      </div>
      {isLoading ? (
        <p className="p-4 text-sm text-muted-foreground">Loading…</p>
      ) : data.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">No active sessions.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">User</TableHead>
                <TableHead className="overline">Role</TableHead>
                <TableHead className="overline">IP</TableHead>
                <TableHead className="overline">Device</TableHead>
                <TableHead className="overline">Signed in</TableHead>
                <TableHead className="overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((s) => (
                <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                  <TableCell>
                    <p className="text-sm font-medium">
                      {s.name || "—"}
                      {s.user_id === me?.id && (
                        <span className="ml-1.5 text-[10px] font-semibold text-primary">(you)</span>
                      )}
                      {s.login_count > 1 && (
                        <span className="ml-1.5 text-[10px] mono px-1 py-0.5 rounded bg-slate-100 text-slate-600">
                          ×{s.login_count} sign-ins
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mono">{s.email}</p>
                  </TableCell>
                  <TableCell className="text-xs capitalize">{(s.role || "").replace(/_/g, " ")}</TableCell>
                  <TableCell className="mono text-xs">{s.ip || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate" title={s.user_agent}>
                    {(s.user_agent || "—").slice(0, 60)}
                  </TableCell>
                  <TableCell className="mono text-xs">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`revoke-sessions-${s.user_id}`}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      disabled={revokeMut.isPending}
                      onClick={() => revoke(s)}
                    >
                      <LogOut className="size-3.5 mr-1" /> Sign out
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

